import React from 'react';
import {Redirect} from 'react-router';
import {Link} from 'react-router-dom';
import layout from '../marketing/marketing.module.css';
import styles from './Home.module.css';
import MarketingHead from '../marketing/components/MarketingHead';
import SiteHeader from '../marketing/components/SiteHeader';
import SiteFooter from '../marketing/components/SiteFooter';
import CtaBand from '../marketing/components/CtaBand';
import Button from '../marketing/components/Button';
import Eyebrow from '../marketing/components/Eyebrow';
import HeadlineLoop from '../marketing/components/HeadlineLoop';
import FeatureCard from '../marketing/components/FeatureCard';
import {HeroProductMockDesktop, HeroProductMockMobile} from '../marketing/illustrations/HeroProductMock';
import {
    VisualiseIllustrationDesktop,
    VisualiseIllustrationMobile,
    ColourCodedIllustration,
    CooperationIllustration,
    JiraIllustrationDesktop,
    JiraIllustrationMobile,
} from '../marketing/illustrations/FeatureIllustrations';
import {VideoThumbnailIllustration} from '../marketing/illustrations/VideoThumbnail';
import {CREATE_PAGE_ROUTE, VIDEOS_ROUTE} from '../marketing/routes';

const Home = ({authentication}) => {
    if (authentication) {
        return <Redirect to="/Pages"/>;
    }

    return (
        <div className={layout.root}>
            <MarketingHead
                title="Ovalview — Making work visible"
                description="Visualise team progress as a simple, colour-coded map of activity. No more wading through endless text to find out where things stand."
            />
            <SiteHeader/>

            <main>
                <section className={styles.hero}>
                    <div className={styles.badge}>
                        <span className={styles.badgeDot}/>
                        <span className={styles.badgeLong}>Integrated with Jira — task status stays in sync</span>
                        <span className={styles.badgeShort}>Integrated with Jira</span>
                    </div>
                    <h1 className={styles.h1}>
                        Making work<br/>
                        <HeadlineLoop color="#30A46C">visible.</HeadlineLoop>
                    </h1>
                    <p className={styles.lead}>
                        Visualise team progress as a simple, colour-coded map of activity. No more wading through
                        endless text to find out where things stand.
                    </p>
                    <div className={styles.heroButtons}>
                        <Button to={CREATE_PAGE_ROUTE} variant="primary" size="hero" icon="arrow">
                            Get started now
                        </Button>
                        <Button href="#videos" variant="secondary" size="hero" icon="play" roundIcon
                                iconPosition="start">
                            Watch video guides
                        </Button>
                    </div>

                    <div className={styles.mockWrap}>
                        <div className={`${styles.mockBox} ${styles.mockDesktopOnly}`}>
                            <HeroProductMockDesktop/>
                        </div>
                        <div className={`${styles.mockBox} ${styles.mockMobileOnly}`}>
                            <HeroProductMockMobile/>
                        </div>
                    </div>
                </section>

                <section className={`${layout.section} ${layout.container}`}>
                    <div className={styles.featuresHeader}>
                        <div className={styles.featuresHeading}>
                            <Eyebrow>Why Ovalview</Eyebrow>
                            <h2 className={styles.h2}>The clarity only a diagram can give.</h2>
                        </div>
                        <p className={styles.featuresLead}>
                            Map every task, who owns it and how far along it is, all on one page your whole team can
                            read at a glance.
                        </p>
                    </div>

                    <div className={styles.featuresGrid}>
                        <FeatureCard
                            span={7}
                            title="Visualise"
                            illustration={
                                <>
                                    <div className={layout.dtOnly}><VisualiseIllustrationDesktop/></div>
                                    <div className={layout.mbOnly}><VisualiseIllustrationMobile/></div>
                                </>
                            }
                        >
                            <p>See your work, not a list of it. Lay tasks out as a map and the dependencies between
                                them become obvious.</p>
                            <Button to={CREATE_PAGE_ROUTE} variant="text" icon="arrow" className={styles.goVisualise}>
                                Go visualise
                            </Button>
                        </FeatureCard>

                        <FeatureCard span={5} title="Colour-coded clarity" illustration={<ColourCodedIllustration/>}>
                            <p>A traffic-light system tells you each task&apos;s status in a glance.</p>
                            <div className={styles.legend}>
                                <span className={styles.legendTodo}>Not done</span>
                                <span className={styles.legendArrow}>&rarr;</span>
                                <span className={styles.legendDoing}>In progress</span>
                                <span className={styles.legendArrow}>&rarr;</span>
                                <span className={styles.legendDone}>Done</span>
                            </div>
                        </FeatureCard>

                        <FeatureCard span={5} title="Cooperation" illustration={<CooperationIllustration/>}>
                            <p>Put a face on every task. See the hand-offs and who&apos;s involved before they
                                happen, and move faster as a team.</p>
                        </FeatureCard>

                        <FeatureCard
                            span={7}
                            dark
                            title="Works with Jira"
                            illustration={
                                <>
                                    <div className={layout.dtOnly}><JiraIllustrationDesktop/></div>
                                    <div className={layout.mbOnly}><JiraIllustrationMobile/></div>
                                </>
                            }
                        >
                            <p className={styles.jiraCardText}>Create stories and tasks automatically. As a task
                                moves on the map, its Jira status updates with it.</p>
                            <div className={styles.legend}>
                                <span className={styles.legendTodoOnDark}>Backlog</span>
                                <span className={styles.legendArrowOnDark}>&rarr;</span>
                                <span className={styles.legendDoingOnDark}>In progress</span>
                                <span className={styles.legendArrowOnDark}>&rarr;</span>
                                <span className={styles.legendDoneOnDark}>Done</span>
                            </div>
                        </FeatureCard>
                    </div>
                </section>

                <section id="videos" className={`${layout.section} ${layout.container} ${styles.videoSection}`}>
                    <div className={styles.videoBand}>
                        <div className={styles.videoCopy}>
                            <Eyebrow>Video guides</Eyebrow>
                            <h2 className={styles.h2}>Rather watch than read?</h2>
                            <p className={styles.videoLead}>Our video guides show Ovalview in action, step by
                                step.</p>
                            <Button to={VIDEOS_ROUTE} variant="primary" size="section" icon="arrow"
                                    className={styles.watchGuides}>
                                Watch the guides
                            </Button>
                        </div>
                        <Link to={VIDEOS_ROUTE} className={styles.videoThumb} aria-label="Play video guide">
                            <VideoThumbnailIllustration/>
                            <span className={styles.playButton}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            </span>
                        </Link>
                    </div>
                </section>

                <div className={layout.section}>
                    <CtaBand
                        heading="See where your work really stands."
                        text="Free to sign up. Make your first map in minutes."
                    >
                        <Button to={CREATE_PAGE_ROUTE} variant="onDark" size="hero" icon="arrow">
                            Get started now
                        </Button>
                        <Button to="/login" variant="outlineOnDark" size="hero">Log in</Button>
                    </CtaBand>
                    <SiteFooter/>
                </div>
            </main>
        </div>
    );
};

export default Home;
